import { useMemo } from 'react';
import PropTypes from 'prop-types';

import { useTheme } from '#/hooks/useTheme';
import { getSeriesColor } from '#/modules/infra/dashboard/helper/dashboardChartTheme';

/**
 * 組成摘要：總計＋細的比例條＋各桶數值與百分比（金額桶、進貨來源、出庫類別共用）。
 * 百分比四捨五入到整數；全為 0 時比例條顯示空軌並註明 `emptyText`。
 * 顏色預設依項目順序取 CATEGORICAL；項目可帶 `colorIndex` 固定取色。
 */
const BreakdownSummary = ({ items, formatValue, totalLabel = '總計', emptyText }) => {
  const { resolvedTheme } = useTheme();

  const rows = useMemo(() => {
    const total = items.reduce((sum, item) => sum + item.value, 0);
    return {
      total,
      items: items.map((item, index) => ({
        ...item,
        percent: total > 0 ? (item.value / total) * 100 : 0,
        color: getSeriesColor(resolvedTheme, item.colorIndex ?? index),
      })),
    };
  }, [items, resolvedTheme]);

  return (
    <div className="flex flex-1 flex-col gap-4 pt-2">
      <div>
        <p className="text-xs text-muted-foreground">{totalLabel}</p>
        <p className="text-2xl font-semibold text-foreground tabular-nums">{formatValue(rows.total)}</p>
      </div>

      <div className="flex h-2.5 w-full overflow-hidden rounded-full bg-muted" aria-hidden>
        {rows.items
          .filter((item) => item.value > 0)
          .map((item) => (
            <span key={item.key} className="h-full" style={{ width: `${item.percent}%`, background: item.color }} />
          ))}
      </div>

      <ul className="flex flex-col divide-y divide-border text-sm">
        {rows.items.map((item) => (
          <li key={item.key} className="flex items-center gap-2 py-2">
            <span className="size-2.5 shrink-0 rounded-full" style={{ background: item.color }} aria-hidden />
            <span className="text-foreground">{item.label}</span>
            <span className="ml-auto text-foreground tabular-nums">{formatValue(item.value)}</span>
            <span className="w-12 text-right text-muted-foreground tabular-nums">{Math.round(item.percent)}%</span>
          </li>
        ))}
      </ul>

      {rows.total === 0 && emptyText && <p className="text-sm text-muted-foreground">{emptyText}</p>}
    </div>
  );
};

BreakdownSummary.propTypes = {
  items: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      value: PropTypes.number.isRequired,
      colorIndex: PropTypes.number,
    }),
  ).isRequired,
  formatValue: PropTypes.func.isRequired,
  totalLabel: PropTypes.string,
  emptyText: PropTypes.string,
};

export default BreakdownSummary;
